import { devSimulatePing } from '~/rage/decorators';
import { ExchangeItemType, VehicleNumberPlateType } from '~/shared';
import { AbstractExchangeService } from './AbstractExchangeService';
import {
	ExchangeState,
	IExchangeItemHouse,
	IExchangeItemMoney,
	IExchangeItemNumberPlate,
	IExchangeItemVehicle,
	IExchangeService,
} from './types';

export class ExchangeServiceStub
	extends AbstractExchangeService
	implements IExchangeService
{
	constructor() {
		super();

		this.setData(
			{
				id: 'test-exchange',
				isCancelled: false,
				state: ExchangeState.PlacingItems,
				tax: 0,
				characters: [
					{
						id: 'local-character',
						name: 'Bob Bebson',
						isConfirmedExchange: false,
						isConfirmedItems: false,
						placedItems: [],
					},
					{
						id: 'second-character',
						name: 'Kek Kokson',
						isConfirmedExchange: false,
						isConfirmedItems: false,
						placedItems: [],
					},
				],
			},
			'local-character',
		);

		this.setOwnedItems([
			{
				id: 'item-1',
				type: ExchangeItemType.Money,
				amount: 50_132_921,
			} as IExchangeItemMoney,
			{
				id: 'item-2',
				type: ExchangeItemType.Vehicle,
				vehicleId: 'vehicle-1',
				vehicleName: 'Infernus',
				model: 'vehicle-config-1',
				numberPlate: {
					type: VehicleNumberPlateType.Classic,
					text: 'a123ab',
					region: '32',
				},
			} as IExchangeItemVehicle,
			{
				id: 'item-3',
				type: ExchangeItemType.Vehicle,
				vehicleId: 'vehicle-2',
				vehicleName: 'Turismo',
				model: 'vehicle-config-2',
				numberPlate: {
					type: VehicleNumberPlateType.Classic,
					text: 'o000oo',
					region: '00',
				},
			} as IExchangeItemVehicle,
			{
				id: 'item-4',
				type: ExchangeItemType.House,
				houseId: '584',
			} as IExchangeItemHouse,
			{
				id: 'item-5',
				type: ExchangeItemType.NumberPlate,
				numberPlate: {
					type: VehicleNumberPlateType.Classic,
					text: 'a777oa',
					region: '77',
				},
			} as IExchangeItemNumberPlate,
			{
				id: 'item-6',
				type: ExchangeItemType.NumberPlate,
				numberPlate: {
					type: VehicleNumberPlateType.Custom,
					text: 'CCDPLANET',
					region: null,
				},
			} as IExchangeItemNumberPlate,
		]);
	}

	public async findPlayers(): Promise<
		{ characterId: string; nickname: string }[]
	> {
		return [{ characterId: 'second-character', nickname: 'Kek Kokson' }];
	}

	@devSimulatePing()
	async invite(otherCharacterId: string): Promise<void> {
		console.log('exchange:invite', { otherCharacterId });
	}

	@devSimulatePing()
	async placeItem(itemId: string, amount?: number | undefined): Promise<void> {
		console.log('exchange:placeItem', { itemId, amount });

		const data = this.getData();
		const character = data.characters.find(
			(char) => char.id === 'local-character',
		);
		if (!character) {
			return;
		}

		const item = this.getOwnedItemsData().find((i) => i.id === itemId);
		if (!item) {
			return;
		}

		character.placedItems.push(item);
		this.setData(data, 'local-character');
		this.setOwnedItems(this.getOwnedItemsData().filter((i) => i.id !== itemId));
	}

	@devSimulatePing()
	async removePlacedItem(itemId: string): Promise<void> {
		console.log('exchange:removePlacedItem', { itemId });

		const data = this.getData();
		const character = data.characters.find(
			(char) => char.id === 'local-character',
		);
		if (!character) {
			return;
		}

		const item = character.placedItems.find((i) => i.id === itemId);
		if (!item) {
			return;
		}

		character.placedItems = character.placedItems.filter(
			(i) => i.id !== itemId,
		);
		this.setData(data, 'local-character');
		this.setOwnedItems([...this.getOwnedItemsData(), item]);
	}

	@devSimulatePing()
	async confirmItems(): Promise<void> {
		console.log('exchange:confirmItems');

		const data = this.getData();
		const character = data.characters.find(
			(char) => char.id === 'local-character',
		);
		if (!character) {
			return;
		}

		character.isConfirmedItems = true;
		this.setData(data, 'local-character');

		setTimeout(() => {
			const data = this.getData();
			data.state = ExchangeState.ConfirmingExchange;
			this.setData(data, 'local-character');
		}, 2000);
	}

	@devSimulatePing()
	async confirmExchange(): Promise<void> {
		console.log('exchange:confirmExchange');

		const data = this.getData();
		const character = data.characters.find(
			(char) => char.id === 'local-character',
		);
		if (!character) {
			return;
		}

		character.isConfirmedExchange = true;
		this.setData(data, 'local-character');

		setTimeout(() => {
			const data = this.getData();
			data.state = ExchangeState.ProcessingItems;
			this.setData(data, 'local-character');

			setTimeout(() => {
				const data = this.getData();
				data.state = ExchangeState.Completed;
				this.setData(data, 'local-character');
			}, 2000);
		}, 2000);
	}

	@devSimulatePing()
	async exitExchange(): Promise<void> {
		console.log('exchange:cancelExchange');

		const data = this.getData();
		data.isCancelled = true;
		data.state = ExchangeState.Completed;
		this.setData(data, 'local-character');
	}
}
